import type { JobItemResultVisibleJob } from '../../helpers';

export type JobResultOrAd = JobItemResultVisibleJob | 'Ad';

type TransformResultsListResult = {
  items: JobResultOrAd[];
  urgentJobsHighlighted: Set<number>;
};

const AD_INDEX = 5;
const URGENT_MAX_INDEX = 20;

const isUrgent =
  (from = 0) =>
  (job: JobItemResultVisibleJob | 'Ad', index: number): boolean =>
    index >= from && job !== 'Ad' && job.jobDetail?.serviceOfferingGroup > 0;

/**
 * Transforms the job search results list by:
 * 1. Duplicating the first urgent job (serviceOfferingGroup > 0) to position 0 for visibility
 * 2. Injecting an ad placeholder at position 5
 * 3. Tracking up to 2 urgent jobs within the first 20 positions for highlighting
 */
export const transformResultsList = (
  jobs: JobItemResultVisibleJob[]
): TransformResultsListResult => {
  const urgentJobsHighlighted = new Set<number>();

  const firstUrgentIndex = jobs.findIndex(isUrgent());
  const hasUrgentJob =
    firstUrgentIndex >= 0 && firstUrgentIndex < URGENT_MAX_INDEX;

  if (hasUrgentJob) {
    urgentJobsHighlighted.add(0);
  }

  const shouldDuplicateFirstUrgent = hasUrgentJob && firstUrgentIndex > 0;

  const updatedJobs = shouldDuplicateFirstUrgent
    ? [jobs[firstUrgentIndex], ...jobs]
    : jobs;

  const firstPart = updatedJobs.slice(0, AD_INDEX);
  const lastPart = updatedJobs.slice(AD_INDEX);
  const ad = ['Ad' as const];
  const items = [...firstPart, ...ad, ...lastPart];

  const offsetFromDuplication = shouldDuplicateFirstUrgent ? 1 : 0;
  const offsetFromAd =
    firstUrgentIndex + offsetFromDuplication >= AD_INDEX ? 1 : 0;
  const searchFromIndex =
    firstUrgentIndex + offsetFromDuplication + offsetFromAd + 1;
  const secondUrgentIndex = items.findIndex(isUrgent(searchFromIndex));

  if (secondUrgentIndex >= 0 && secondUrgentIndex < URGENT_MAX_INDEX) {
    urgentJobsHighlighted.add(secondUrgentIndex);
  }

  return {
    items,
    urgentJobsHighlighted,
  };
};
